<template>
  <div class="share-view-container" :class="{ 'is-mobile': isMobile }">
    <div v-if="loading" class="share-view-loading">
      <i class="el-icon-loading"></i>
      <p>正在加载文档...</p>
    </div>
    
    <div v-else-if="errorMessage" class="share-view-error">
      <i class="el-icon-warning-outline"></i>
      <p>{{ errorMessage }}</p>
      <!-- <el-button type="primary" @click="reload">刷新重试</el-button> -->
    </div>
    
    <div v-else class="share-view-content">
      <header class="share-view-header">
        <div>
          <h1 class="share-view-title">{{ document.title }}</h1>
          <div class="share-view-meta">
            <span>
              <i class="el-icon-user"></i>
              {{ document.creatorName || '未知作者' }}
            </span>
            <span>
              <i class="el-icon-time"></i>
              创建：{{ formatDate(document.createTime) }}
            </span>
            <span>
              <i class="el-icon-edit"></i>
              更新：{{ formatDate(document.updateTime) }}
            </span>
          </div>
        </div>
        <!-- <div class="share-view-actions">
          <el-tag type="success" size="medium">分享链接</el-tag>
          <el-tooltip content="复制当前链接" placement="bottom">
            <el-button
              type="text"
              icon="el-icon-document-copy"
              @click="copyCurrentUrl"
            ></el-button>
          </el-tooltip>
        </div> -->
      </header>
      
      <main class="share-view-main">
        <div
          class="share-view-scroll"
          ref="contentWrapper"
        >
          <article class="share-view-article">
            <div class="share-view-body ql-editor" v-html="document.content" @click="handleContentClick"></div>
          </article>
        </div>
      </main>
    </div>
    
    <!-- 预览组件（仅移动端使用） -->
    <PreviewImageOverlay 
      v-if="previewOverlay.visible && isMobile"
      :src="previewOverlay.src"
      @close="previewOverlay.visible = false"
    />
    
    <!-- 图片查看器（仅电脑端使用） -->
    <ElImageViewer
      v-if="showImageViewer && !isMobile"
      :on-close="closeImageViewer"
      :url-list="previewImages"
      :initial-index="currentImageIndex"
    />
  </div>
</template>

<script>
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import { DOCUMENT_SHARE_PATHS } from '@/api/paths'
import PreviewImageOverlay from '@/components/PreviewImageOverlay.vue'
import ElImageViewer from 'element-ui/packages/image/src/image-viewer'

export default {
  name: 'ShareView',
  components: {
    PreviewImageOverlay,
    ElImageViewer
  },
  data() {
    return {
      loading: false,
      document: {
        title: '',
        content: '',
        creatorName: '',
        createTime: null,
        updateTime: null
      },
      shareToken: '',
      errorMessage: '',
      screenWidth: window.innerWidth,
      showImageViewer: false,
      previewImages: [],
      currentImageIndex: 0,
      previewOverlay: {
        visible: false,
        src: ''
      }
    }
  },
  computed: {
    isMobile() {
      // 优先使用设备检测，避免横屏时被误判为桌面端
      if (typeof navigator !== 'undefined') {
        const ua = navigator.userAgent || navigator.vendor || ''
        
        // 1. 检测明确的移动设备标识
        const mobileRegex = /(android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini)/i
        if (mobileRegex.test(ua)) {
          return true
        }
        
        // 2. 检测移动设备的User Agent特征
        const mobilePlatforms = /Mobile|mobile|Mobi|mobi/i
        if (mobilePlatforms.test(ua)) {
          return true
        }
        
        // 3. 检测触摸设备并结合屏幕尺寸
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
          // 使用屏幕的最小尺寸来判断，避免横屏影响
          const screenMinSize = window.screen ? Math.min(window.screen.width, window.screen.height) : 0
          const screenMaxSize = window.screen ? Math.max(window.screen.width, window.screen.height) : 0
          
          // 如果最小尺寸小于等于768，或者最大尺寸小于等于1024，很可能是移动设备
          if (screenMinSize <= 768 || (screenMaxSize <= 1024 && screenMinSize <= 768)) {
            return true
          }
        }
      }
      
      // 4. 兜底：如果以上都不满足，但当前窗口宽度小于等于768，也认为是移动端
      // 这主要是为了处理一些特殊情况和桌面端的小窗口
      return this.screenWidth <= 768
    }
  },
  mounted() {
    this.shareToken = this.$route.params.token
    if (!this.shareToken) {
      this.errorMessage = '分享链接无效'
      return
    }
    this.loadDocument()
    // 屏幕宽度监听用于区分移动端与桌面端
    this.__onResize = () => { this.screenWidth = window.innerWidth }
    window.addEventListener('resize', this.__onResize)
  },
  beforeDestroy() {
    if (this.__onResize) {
      window.removeEventListener('resize', this.__onResize)
    }
  },
  methods: {
    async loadDocument() {
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await this.$get(DOCUMENT_SHARE_PATHS.VIEW_SHARE, {
          params: { token: this.shareToken }
        })
        if (response.data && response.data.code === 200) {
          const data = response.data.data
          const doc = data.document || {}
          this.document = {
            title: doc.title || '未命名文档',
            content: doc.content || '<p class="empty-tip">暂无内容</p>',
            creatorName: doc.creatorName || '',
            createTime: doc.createTime || null,
            updateTime: doc.updateTime || null
          }
        } else {
          this.errorMessage = response.data.msg || '加载文档失败'
        }
      } catch (error) {
        this.$message.error('加载文档失败')
        console.error(error)
        this.errorMessage = '加载文档失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    reload() {
      this.loadDocument()
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return '-'
      }
      return date.toLocaleString('zh-CN')
    },
    copyCurrentUrl() {
      const dummy = document.createElement('input')
      dummy.value = window.location.href
      document.body.appendChild(dummy)
      dummy.select()
      try {
        document.execCommand('copy')
        this.$message.success('链接已复制')
      } catch (error) {
        this.$message.error('复制失败，请手动复制链接')
      }
      document.body.removeChild(dummy)
    },
    // 处理内容点击事件
    handleContentClick(event) {
      if (event.target.tagName === 'IMG') {
        const img = event.target
        if (this.isMobile) {
          // 移动端使用 PreviewImageOverlay
          this.previewOverlay.src = img.src
          this.previewOverlay.visible = true
        } else {
          // 电脑端使用 Element UI 的图片查看器
          // 收集所有图片
          const allImages = this.$el.querySelectorAll('.share-view-body img')
          this.previewImages = Array.from(allImages).map(imgEl => imgEl.src)
          this.currentImageIndex = Array.from(allImages).indexOf(img)
          this.showImageViewer = true
        }
      }
    },
    // 关闭图片预览
    closeImageViewer() {
      this.showImageViewer = false
    }
  }
}
</script>

<style scoped>
.share-view-container {
  min-height: 100vh;
  background: #f5f7f9;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px 15px;
}

.share-view-loading,
.share-view-error {
  text-align: center;
  color: #606266;
}

.share-view-loading i {
  font-size: 36px;
  color: #409EFF;
  display: block;
  margin-bottom: 12px;
}

.share-view-error i {
  font-size: 40px;
  color: #f56c6c;
  display: block;
  margin-bottom: 12px;
}

.share-view-content {
  width: 100%;
  max-width: 1080px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.share-view-header {
  padding: 24px 32px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.share-view-title {
  margin: 0 0 16px 0;
  font-size: 28px;
  font-weight: 600;
  color: #303133;
  word-break: break-word;
}

.share-view-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: #909399;
  font-size: 14px;
}

.share-view-meta span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.share-view-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.share-view-main {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.share-view-scroll {
  flex: 1;
  max-height: 70vh;
  overflow-y: auto;
  padding: 24px 32px 40px;
}

.share-view-article {
  background: #fff;
  border-radius: 8px;
}

.share-view-body {
  font-size: 16px;
  line-height: 1.8;
  color: #303133;
}

.share-view-body ::v-deep img {
  max-width: 100%;
  max-height: 380px;
  width: auto;
  height: auto;
  margin: 18px 0;
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  cursor: zoom-in;
  object-fit: contain;
}

.share-view-body ::v-deep pre {
  background: #282c34;
  color: #fff;
  padding: 16px;
  border-radius: 6px;
  overflow: auto;
}

.share-view-body ::v-deep code {
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
}

.share-view-container.is-mobile {
  padding: 15px 10px;
}

.share-view-container.is-mobile .share-view-header {
  padding: 18px 20px;
  flex-direction: column;
  align-items: flex-start;
}

.share-view-container.is-mobile .share-view-scroll {
  max-height: none;
  padding: 18px 20px 30px;
}

.share-view-container.is-mobile .share-view-title {
  font-size: 22px;
}

.share-view-container.is-mobile .share-view-meta {
  flex-direction: column;
  gap: 10px;
}

.share-view-scroll::-webkit-scrollbar {
  width: 8px;
}

.share-view-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.share-view-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}
</style>
